<template>
  <b-card
    v-if="record && canChangeStatus && !completed"
    class="RealisationStatusPanel"
  >
    <h4>{{ $t('processes.sidebar.change_status') }}</h4>

    <p v-if="cancelled">
      {{ $t('processes.sidebar.process_is_cancelled') }}
    </p>
    <p v-if="onhold">
      {{ $t('processes.sidebar.process_is_onhold') }}
    </p>
    <p v-if="locationUnavailable">
      {{ locationUnavailableMessage }}
    </p>

    <div class="d-flex justify-content-between align-items-end">
      <b-button
        :disabled="busy || cancelled"
        :variant="action === 'onhold' ? 'warning' : 'outline-warning'"
        class="mr-2"
        @click="setAction({ action: onhold ? 'resume' : 'onhold' })"
      >
        {{ onhold ? $t('processes.sidebar.process_resume') : $t('processes.sidebar.process_put_onhold') }}
      </b-button>
      <b-button
        :disabled="busy || locationUnavailable"
        :variant="action === 'cancel' ? 'danger' : 'outline-danger'"
        @click="setAction({ action: cancelled ? 'restore' : 'cancel' })"
      >
        {{ cancelled ? $t('processes.sidebar.process_status_restore') : $t('processes.sidebar.process_status_cancel') }}
      </b-button>
    </div>

    <Feedback
      class="mt-2"
      :feedback="feedback"
    />

    <transition>
      <div v-if="action">
        <div v-if="['onhold', 'cancel'].includes(action)">
          <b-select
            v-model="reason"
            :disabled="busy"
            :options="reasonOptions"
            class="mt-2"
          />
          <div
            v-if="action && action !== 'restore'"
            class="pt-1"
          >
            {{ $t('processes.sidebar.status_remark') }}
          </div>
          <b-textarea
            v-model="comment"
            :disabled="busy"
            class="mt-2"
          />
        </div>
        <b-button
          :disabled="busy"
          variant="dark"
          class="w-100 mt-2"
          @click="handleConfirm"
        >
          {{ actionConfirmLabel }}
        </b-button>
      </div>
    </transition>
  </b-card>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import Process from '@/models/Process';
import Feedback from '@/components/form/Feedback.vue';

import { multilineUnicodeString, getInvalidCharacters } from '@/services/validation';

export default {
  components: {
    Feedback
  },
  props: {
    uuid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      record: null,
      busy: false,
      location: null,

      action: null,
      actionReason: null,
      comment: '',

      feedback: {
        show: false,
        message: ''
      }
    };
  },
  computed: {
    ...mapGetters('realisations', ['getSelectedRecord']),
    ...mapGetters('user', ['canChangeStatus']),
    ...mapGetters('chargingpoints', ['getChargingPointByUUID']),
    ...mapGetters('tenant', ['isCurrentTenant', 'getOnHoldReasons', 'getCancelReasons']),
    ...mapGetters('processes', ['getSelectedProcess']),
    locationUnavailable() {
      try {
        if (!this.cancelled) {
          return false;
        }

        // If the location is connected to this RP, the location is available to this RP
        return this.location && this.location.workflowUuid === this.uuid

        //return this.location && !['definitive', 'suggestion'].includes(this.location.data.properties.status);
      } catch (e) {
        return true;
      }
    },

    isProcess () {
      return this.record.processType?.value !== 'realisation'
    },

    completed() {
      try {
        return !!this.record.status.completed;
      } catch (e) {
        return false;
      }
    },
    onhold() {
      try {
        return !!this.record.status.onhold;
      } catch (e) {
        return false;
      }
    },
    cancelled() {
      try {
        return !!this.record.status.cancelled;
      } catch (e) {
        return false;
      }
    },

    cancelReason() {
      try {
        return this.actionReason ? this.actionReason : this.record.statusMeta.cancelled.reason;
      } catch (e) {
        return null;
      }
    },
    onholdReason() {
      try {
        return this.actionReason ? this.actionReason : this.record.statusMeta.onhold.reason;
      } catch (e) {
        return null;
      }
    },

    reason: {
      get() {
        return this.action === 'cancel' ? this.cancelReason : this.onholdReason;
      },
      set(value) {
        this.actionReason = value;
      }
    },
    reasonOptions() {
      if (this.action === 'cancel') {
        return this.getCancelReasons.filter(reason =>
         reason.forProcessTypes ? reason.forProcessTypes.includes(this.record.processType.value)
          : true
        )
      }

      return this.getOnHoldReasons.filter(reason =>
         reason.forProcessTypes ? reason.forProcessTypes.includes(this.record.processType.value)
          : true
        )
    },

    actionConfirmLabel() {
      switch (true) {
        case this.action === 'restore':
          return this.$t('processes.sidebar.process_status_restore');
        case this.action === 'cancel':
          return this.$t('processes.sidebar.process_status_cancel');
        case this.action === 'resume':
          return this.$t('processes.sidebar.process_resume');
        case this.action === 'onhold':
          return this.$t('processes.sidebar.process_put_onhold');
      }

      return this.$t('processes.sidebar.confirm');
    },
    isCustomProcess () {
      return ['brussels', 'wallonia', 'takeover'].includes(this.record.processType.value)
    },
    locationUnavailableMessage() {
      const rejected = this.location.properties.status === 'rejected';
      return rejected
        ? this.$t('processes.sidebar.location_unavailable_rejected')
        : this.$t('processes.sidebar.location_unavailable_linked');
    }
  },
  watch: {
    action: {
      immediate: true,
      handler() {
        this.reason = '';
        this.comment = '';
      }
    }
  },
  created() {
    this.record = this.getSelectedProcess || this.getSelectedRecord;
    const currentLocation = this.record.CurrentLocation || this.record.values.Location
    if (this.record) {
      this.location = this.getChargingPointByUUID({
        uuid: currentLocation.Location?.chargingpointUuid ||
              currentLocation.location?.chargingStation?.chargingpointUuid
      });
    }
  },
  methods: {
    ...mapMutations('realisations', ['replaceRecord']),
    ...mapMutations('chargingpoints', ['replaceLocation']),
    setAction({ action }) {
      this.action = this.action === action ? null : action;
    },
    handleConfirm: async function () {
      try {
        const ref = this.record.ref || false;

        if (!ref) return;

        this.busy = true;

        // TODO: Validation of reason & comment when relevant (e.g. action = onhold || cancel)
        if (['onhold', 'cancel'].includes(this.action)) {
          if (!this.reason) {
            this.feedback = {
              show: true,
              variant: 'danger',
              message: this.$t('processes.sidebar.reason_required')
            };
            this.busy = false;
            return;
          }

          if (
            (this.action === 'onhold' && !this.getOnHoldReasons.map(reason => reason.value).includes(this.reason)) ||
            (this.action === 'cancel' && !this.getCancelReasons.map(reason => reason.value).includes(this.reason))
          ) {
            this.feedback = {
              show: true,
              variant: 'danger',
              message: this.$t('processes.sidebar.valid_reason_required')
            };
            this.busy = false;
            return;
          }

          if (this.reason === 'other' && !this.comment) {
            this.feedback = {
              show: true,
              variant: 'danger',
              message: this.$t('processes.sidebar.comment_required')
            };
            this.busy = false;
            return;
          }

          if (this.comment !== '') {
            if (this.comment.length > 300) {
              this.feedback = {
                show: true,
                variant: 'danger',
                message: this.$t('processes.sidebar.comment_too_long')
              };
              this.busy = false;
              return;
            }
            if (!multilineUnicodeString(this.comment)) {
              this.feedback = {
                show: true,
                variant: 'danger',
                message: this.$t('processes.sidebar.comment_invalid_chars', { chars: getInvalidCharacters(this.comment) })
              };
              this.busy = false;
              return;
            }
          }
        }

        let data = {
          caseRef: this.record.case_ref,
          step: 'status',
          action: this.action,
          reason: this.reason,
          comment: this.comment
        };

        // Make the call
        const token = await this.$auth.getTokenSilently();
        const endpoint = this.isProcess ? this.isCustomProcess ? 'custom_process' : 'process' : 'realisation'
        this.$store.dispatch('processes/isSavingProcess', true)

        const api = await fetch(`/api/${endpoint}_status`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            ref,
            data
          })
        });
        if (!api.ok) {
          // console.log(api)
          throw new Error('Unable to update the status');
        }
        const response = await api.json();

        this.$store.dispatch('processes/isSavingProcess', false)

        this.feedback = {
          show: true,
          variant: 'success',
          message: this.$t('processes.sidebar.status_saved')
        };

        const process = response.data.record

        if (this.isProcess) {
          process.realisationProcessData = this.getSelectedProcess.realisationProcessData

          this.$store.dispatch('processes/setSelectedProcess', {
            process: new Process({ data: process, ref: process._id })
          })
          this.$store.dispatch('processes/replaceProcess', { process })

        } else {
          this.$store.dispatch('realisations/setSelectedRecord', { record: process })
        }

        if (Array.isArray(response.data.locations)) {
          response.data.locations.forEach(location => {
            this.replaceLocation({ location });
          });
        }

        //
        this.action = null;
        this.busy = false;
        this.$emit('update-component');
      } catch (e) {
        console.log(e);

        this.busy = false;

        this.feedback = {
          show: true,
          variant: 'danger',
          message: this.$t('processes.sidebar.status_not_saved')
        };
      }
    }
  }
};
</script>

<style lang="scss">
.RealisationStatusPanel {
  h4,
  p {
    text-align: center;
  }
}
</style>
